/**
 * Referral Code Card
 * Displays referral code, link, and QR code with copy functionality
 */

import { useState, useRef } from 'react';
import { Copy, Check, Download, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { copyToClipboard, getReferralUrl } from '../utils/helpers';
import { COPY_FEEDBACK_DURATION, QR_CONFIG } from '../utils/constants';

interface ReferralCodeCardProps {
  referralCode: string;
  isLoading?: boolean;
}

export function ReferralCodeCard({ referralCode, isLoading }: ReferralCodeCardProps) {
  const { t } = useLanguage();
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const referralUrl = getReferralUrl(referralCode);

  const handleCopyCode = async () => {
    if (await copyToClipboard(referralCode)) {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), COPY_FEEDBACK_DURATION);
    }
  };

  const handleCopyLink = async () => {
    if (await copyToClipboard(referralUrl)) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), COPY_FEEDBACK_DURATION);
    }
  };

  const handleDownloadQR = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    canvas.width = QR_CONFIG.DOWNLOAD_SIZE;
    canvas.height = QR_CONFIG.DOWNLOAD_SIZE;

    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const link = document.createElement('a');
        link.download = `rumio-referral-${referralCode}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
        {/* Code Section */}
        <div className="p-6">
          <div className="mb-1">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('referrals.yourReferralCode', 'Your Referral Code')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('referrals.shareCodeWithFriends', 'Share this code with friends')}
            </p>
          </div>

          {/* Code Display */}
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 bg-gray-100 rounded-xl p-4 font-mono text-xl text-center font-bold tracking-widest">
              {referralCode || t('common.loading', 'Loading...')}
            </div>
            <button
              onClick={handleCopyCode}
              disabled={!referralCode}
              className={`p-4 rounded-xl transition-all duration-200 ${
                codeCopied
                  ? 'bg-green-500 text-white'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              } disabled:bg-gray-300 disabled:cursor-not-allowed`}
              title={t('common.copy', 'Copy')}
            >
              {codeCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>

          {/* Link Display */}
          <div className="mt-6">
            <label htmlFor="referral-link" className="text-sm font-medium text-gray-700 mb-2 block">
              {t('referrals.referralLink', 'Referral Link')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="referral-link"
                type="text"
                readOnly
                value={referralUrl}
                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
              <button
                onClick={handleCopyLink}
                disabled={!referralUrl}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  linkCopied
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {linkCopied ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
              </button>
            </div>
          </div>
        </div>

        {/* QR Code Section */}
        <div className="p-6 flex flex-col items-center justify-center bg-gray-50/50">
          <p className="text-sm font-medium text-gray-700 mb-4">
            {t('referrals.qrCode', 'QR Code')}
          </p>

          <div ref={qrRef} className="bg-white p-4 rounded-2xl border-2 border-gray-100 shadow-sm">
            {referralUrl ? (
              <QRCodeSVG
                value={referralUrl}
                size={QR_CONFIG.SIZE}
                level={QR_CONFIG.LEVEL}
                includeMargin
                bgColor={QR_CONFIG.BG_COLOR}
                fgColor={QR_CONFIG.FG_COLOR}
              />
            ) : (
              <div className="w-[200px] h-[200px] flex items-center justify-center bg-gray-100 rounded">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            )}
          </div>

          <button
            onClick={handleDownloadQR}
            disabled={!referralUrl}
            className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span className="text-sm font-medium">{t('referrals.downloadQrCode', 'Download PNG')}</span>
          </button>

          <p className="text-xs text-gray-500 mt-2 text-center">
            {t('referrals.scanToVisit', 'Scan to visit your referral link')}
          </p>
        </div>
      </div>
    </div>
  );
}
